import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Wine, Sparkles, Award, MapPin } from 'lucide-react';

const Wines = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const wines = [
    {
      name: "Varietal Tannat",
      type: "Tinto",
      description: "Nuestro emblema. Robusto, con notas de frutos rojos y especias.",
      awards: "Medalla de Oro 2023",
      image: "https://customer-assets.emergentagent.com/job_vineyardelegance/artifacts/gdtbpyuz_2.png"
    },
    {
      name: "Cabernet Franc",
      type: "Rosado",
      description: "Fresco y afrutado, perfecto para disfrutar al aire libre.",
      awards: "Recomendado Catad'Or",
      image: "https://customer-assets.emergentagent.com/job_vineyardelegance/artifacts/nm6yvzoe_4.png"
    },
    {
      name: "Cabernet Sauvignon",
      type: "Tinto",
      description: "Elegante y estructurado, con toques de madera y especias.",
      awards: "90 pts Wine Spectator",
      image: "https://customer-assets.emergentagent.com/job_vineyardelegance/artifacts/mw0psibf_7.png"
    },
    {
      name: "Merlot",
      type: "Tinto",
      description: "Suave y aterciopelado, con notas de ciruela y vainilla.",
      awards: "Mejor Vino Uruguay 2023",
      image: "https://customer-assets.emergentagent.com/job_vineyardelegance/artifacts/el4dsvvr_11.png"
    },
    {
      name: "Sauvignon Blanc",
      type: "Blanco",
      description: "Fresco y aromático, con toques de frutas cítricas y hierbas.",
      awards: "Recomendado Sommelier",
      image: "https://customer-assets.emergentagent.com/job_vineyardelegance/artifacts/2fsvkexm_16.png"
    }
  ];

  return (
    <section id="wines" className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-700/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-700/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Nuestros Vinos
          </h2>
          <div className="w-24 h-1 mx-auto mb-6" style={{background: 'linear-gradient(90deg, #D4AF37 0%, #C9A961 100%)'}}></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            La expresión viva de nuestra tierra. Cada botella refleja el esfuerzo dedicado 
            en el cultivo y el respeto por la tipicidad de nuestras variedades.
          </p>
        </motion.div>

        {/* Wines Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto">
          {wines.map((wine, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -10 }}
              className="group relative bg-white rounded-2xl shadow-xl overflow-hidden"
              data-testid={`wine-card-${index}`}
            >
              {/* Wine Bottle Image */}
              <div className="h-80 bg-gradient-to-b from-gray-50 to-white relative flex items-center justify-center overflow-hidden p-8">
                <img 
                  src={wine.image} 
                  alt={wine.name}
                  className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                />
                <div className="backdrop-blur-sm px-3 py-1 rounded-full text-white text-sm font-medium absolute top-4 right-4" style={{background: 'rgba(212, 175, 55, 0.9)'}}>
                  {wine.type}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{wine.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{wine.description}</p>
                
                <div className="flex items-center gap-2 text-sm" style={{color: '#D4AF37'}}>
                  <Award className="h-4 w-4" />
                  <span className="font-medium">{wine.awards}</span>
                </div>
              </div>

              {/* Hover Effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6"
                initial={false}
              >
                <button 
                  className="w-full text-white py-2 rounded-full font-bold transition-colors"
                  style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
                  data-testid={`wine-info-btn-${index}`}
                >
                  Ver Detalles
                </button>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 text-white shadow-2xl border-t-4"
          style={{borderTopColor: '#D4AF37'}}
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <Sparkles className="h-12 w-12 mb-4" style={{color: '#D4AF37'}} />
              <h3 className="text-3xl font-bold mb-4">Vinos que reflejan nuestro terroir</h3>
              <p className="text-gray-300 mb-6">
                Creamos vinos que reflejan la identidad y el carácter del terroir uruguayo. 
                Cada botella es el resultado de años de dedicación y pasión por la vitivinicultura.
              </p>
              <div className="flex items-center gap-2" style={{color: '#D4AF37'}}>
                <MapPin className="h-5 w-5" />
                <span>Cuchilla Pereyra, Montevideo</span>
              </div>
            </div>
            
            <div className="relative h-64 rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1562601579-599dec564e06?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Bodega de vinos"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Wines;
